import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import UserAvatar from "./components/UserAvatar";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FastForwardIcon from "@mui/icons-material/FastForward";
import DeleteIcon from "@mui/icons-material/Delete";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import { tableHeaderCellSx } from "./components/DataCard";
import FileViewer from "./FileViewer";
import apiClient from "./utils/apiClient";
import usePaginatedList from "./hooks/usePaginatedList";
import { useEvaluationStore } from "./store/evaluationStore";
import { useAuthStore } from "./store/authStore";
import { formatCost, formatUsage, totalCost } from "./utils/usage";

// Status pill on the Runs row's evaluation cell. Kept local so the file
// doesn't need to import EvaluationChatPanel or duplicate its palette.
const RUN_STATUS_LABEL = { open: "Open", in_progress: "In Progress", closed: "Closed" };
const RUN_STATUS_COLOR = { open: "info", in_progress: "warning", closed: "success" };

// Poll while the table is open so admins can watch live progress without
// hand-refreshing. Subprocess heartbeat plus run.start_datetime ticks visibly.
const REFRESH_INTERVAL_MS = 5000;

const STATUS_COLORS = {
  queued: "default",
  started: "info",
  running: "info",
  completed: "success",
  error: "error",
  stopped: "warning",
};

// Mirrors pipeline.py's TERMINAL_RUN_STATUSES -- everything else (queued,
// started, the dynamic "process N of M" a run spends most of its actual
// life in, post-processing, BioInterpreter, ...) counts as active. This
// used to be an ACTIVE_STATUSES allowlist of just {queued, started,
// running} — "process N of M" (and post-processing/BioInterpreter) was
// never in it, so the Stop button was disabled for nearly a run's entire
// real duration, not just its first moment.
const TERMINAL_STATUSES = new Set(["completed", "complete", "error", "stopped", "cancelled"]);

// The Actions column is the one thing on this row every admin actually needs
// to click. At 11 columns wide this table routinely overflows the viewport
// (TableContainer's overflowX: auto), which used to push Actions off-screen
// behind an easy-to-miss horizontal scroll — "actions are not usable" was a
// direct bug report, not a style nitpick. Freezing the column via CSS
// position: sticky keeps it visible at the right edge regardless of scroll
// position; the left box-shadow stands in for a border so it reads as
// detached from whatever data column is currently scrolled underneath it.
const stickyActionsCellSx = {
  position: "sticky",
  right: 0,
  boxShadow: "-6px 0 6px -6px rgba(15, 23, 42, 0.15)",
};

// Complement to stickyActionsCellSx: freezes the leading "which row is this"
// column (Status) on the left so identity doesn't scroll out of view either
// — same underlying complaint, the other end of the table. Only one column
// frozen here, deliberately: stacking a second sticky column (e.g. User)
// next to it would need a hardcoded left-offset equal to the Status column's
// rendered width, which isn't stable — the status Chip's width varies with
// the label text ("completed" vs "error" vs "queued").
const stickyStatusCellSx = {
  position: "sticky",
  left: 0,
  boxShadow: "6px 0 6px -6px rgba(15, 23, 42, 0.15)",
};

// Condensed start-time formatter: HH:MM:SS when the run started today,
// "MMM DD HH:MM" otherwise. Saves horizontal space so the action buttons
// fit without horizontal scroll.
const fmtStarted = (epoch) => {
  if (epoch == null || epoch === "") return "—";
  const n = Number(epoch);
  if (!Number.isFinite(n)) return String(epoch);
  const ms = n < 1e12 ? n * 1000 : n;
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  const pad = (x) => String(x).padStart(2, "0");
  if (sameDay) {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getMonth()]} ${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toMs = (epoch) => {
  const n = Number(epoch);
  return n < 1e12 ? n * 1000 : n;
};

const elapsed = (startEpoch, endEpoch) => {
  if (startEpoch == null) return "—";
  const startMs = toMs(startEpoch);
  const endMs = endEpoch != null && endEpoch !== "" ? toMs(endEpoch) : Date.now();
  let s = Math.max(0, Math.floor((endMs - startMs) / 1000));
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// Grand-total span for the Timing breakdown dialog: earliest known start
// (queued_at, else start_datetime) through the latest known finish across
// the run's own end_datetime AND every subtask's finished_at. An on-demand
// action (e.g. a BioInterpreter variant run started well after the main
// pipeline already finished) can complete long after run.end_datetime, so
// Total can't just be elapsed(start_datetime, end_datetime) — that silently
// drops any on-demand stage's time entirely.
const totalElapsed = (run) => {
  if (!run) return "—";
  const startCandidates = [run.queued_at, run.start_datetime].filter((v) => v != null);
  if (startCandidates.length === 0) return "—";
  const startMs = Math.min(...startCandidates.map(toMs));

  const subtaskStates = Object.values(run.subtasks || {}).filter(Boolean);
  const stillRunning = run.end_datetime == null || subtaskStates.some((s) => s.finished_at == null);
  if (stillRunning) return elapsed(startMs, null);

  const endCandidates = [run.end_datetime, ...subtaskStates.map((s) => s.finished_at)]
    .filter((v) => v != null && v !== "");
  const endMs = Math.max(...endCandidates.map(toMs));
  return elapsed(startMs, endMs);
};

// Friendly labels for known run.subtasks keys (see subtask_util.py /
// pipeline.py). Any key not listed here (e.g. a future biointerpreter
// variant, "biointerpreter-<name>") falls back to a humanized version of the
// raw key — no frontend change needed when a new stage/variant starts
// showing up in the data.
const STAGE_LABELS = {
  matlab: "MATLAB",
  post_processing: "Post-processing",
  biointerpreter: "BioInterpreter",
  interpreter: "Report Interpreter",
};

const stageLabel = (key) => {
  if (STAGE_LABELS[key]) return STAGE_LABELS[key];
  if (key.startsWith("biointerpreter-")) return `BioInterpreter (${key.slice("biointerpreter-".length)})`;
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// Fixed display order for the stages we know about; anything else (a new
// subtask key not yet listed in STAGE_LABELS) is appended after, sorted.
const STAGE_ORDER = ["matlab", "post_processing", "biointerpreter", "interpreter"];

export default function Runs() {
  const [busyId, setBusyId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingStop, setPendingStop] = useState(null);
  const [pendingRestart, setPendingRestart] = useState(null);
  const [pendingReplay, setPendingReplay] = useState(null);
  const [timingRun, setTimingRun] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  // Only Admins can toggle the "Evaluate" flag (run_api.py method_roles
  // locks PUT /run/{id} to Admin) — InternalUser sees the same column
  // read-only, same as the BioInterpreter cell below.
  const currentUserRoles = useAuthStore((state) => state.user?.roles) || [];
  const isAdmin = Array.isArray(currentUserRoles)
    ? currentUserRoles.includes("Admin")
    : currentUserRoles === "Admin";

  // Server-side pagination. The run-list route is paginated, so
  // run_service.get_page_run pushes ORDER BY (activity desc) / LIMIT / OFFSET /
  // COUNT down into Postgres — the full (large) run table is never shipped to
  // the browser. Polling refreshes the current page in place.
  const {
    records: rows,
    total,
    loading,
    error: fetchError,
    page,
    setPage,
    pageSize: rowsPerPage,
    setPageSize: setRowsPerPage,
    q: filter,
    setQ: setFilter,
    refresh,
  } = usePaginatedList("/run-list", {
    initialPageSize: 25,
    initialSort: "activity",
    initialOrder: "desc",
    pollMs: REFRESH_INTERVAL_MS,
  });
  const error = fetchError ? "Failed to load runs. Please try again." : null;

  // Compatibility shim so the action handlers below read naturally.
  const fetchRuns = useCallback(() => refresh(), [refresh]);

  // Load evaluation envelopes so each Runs row can render its status /
  // avg rating chip. Cached in the store (30s) so paging around the
  // table doesn't re-fetch. Indexed by report_id for O(1) row lookup.
  const evaluationRows = useEvaluationStore((s) => s.all);
  const fetchAllEvaluations = useEvaluationStore((s) => s.fetchAll);
  useEffect(() => { fetchAllEvaluations(false); }, [fetchAllEvaluations]);
  const evaluationByReport = useMemo(() => {
    const map = new Map();
    for (const e of evaluationRows || []) {
      if (e?.report_id) map.set(e.report_id, e);
    }
    return map;
  }, [evaluationRows]);

  // Filtering, activity-desc sorting, and slicing all happen server-side now
  // (run_service.get_page_run); `rows` is already the current page in order.
  const paged = rows;

  const doStop = async (run) => {
    setBusyId(run.id);
    try {
      const res = await apiClient.post("/run-request", { action: "stop", id: run.id });
      const subprocessKilled = res?.metadata?.subprocess_killed;
      setActionResult({
        severity: subprocessKilled ? "success" : "info",
        text: subprocessKilled
          ? `Run ${run.id} subprocess killed and marked stopped.`
          : `Run ${run.id} had no live subprocess (queued or already finished); marked stopped.`,
      });
      await fetchRuns({ showSpinner: false });
    } catch (err) {
      console.error("stop failed", err);
      setActionResult({ severity: "error", text: `Stop failed: ${err.message || err}` });
    } finally {
      setBusyId(null);
      setPendingStop(null);
    }
  };

  const doRestart = async (run) => {
    setBusyId(run.id);
    try {
      const res = await apiClient.post("/run-request", { action: "restart", id: run.id });
      const newId = res?.records?.[0]?.id;
      setActionResult({
        severity: "success",
        text: newId
          ? `Restarted as new run ${newId} (launched as ${run.user_id}).`
          : `Restart accepted.`,
      });
      await fetchRuns({ showSpinner: false });
    } catch (err) {
      console.error("restart failed", err);
      setActionResult({ severity: "error", text: `Restart failed: ${err.message || err}` });
    } finally {
      setBusyId(null);
      setPendingRestart(null);
    }
  };

  const doReplay = async (run) => {
    setBusyId(run.id);
    try {
      const res = await apiClient.post("/run-request", { action: "replay", source_report_id: run.id });
      const newId = res?.records?.[0]?.id;
      setActionResult({
        severity: newId ? "success" : "error",
        text: newId
          ? `Replaying as new run ${newId} — MATLAB skipped, using ${run.id}'s Summary_outputs.`
          : `Replay failed: ${res?.metadata?.error || "unknown error"}`,
      });
      await fetchRuns({ showSpinner: false });
    } catch (err) {
      console.error("replay failed", err);
      setActionResult({ severity: "error", text: `Replay failed: ${err.message || err}` });
    } finally {
      setBusyId(null);
      setPendingReplay(null);
    }
  };

  const doDelete = async (run) => {
    setBusyId(run.id);
    try {
      await apiClient.delete(`/run/${encodeURIComponent(run.id)}`);
      setActionResult({ severity: "success", text: `Deleted run ${run.id}.` });
      await fetchRuns({ showSpinner: false });
    } catch (err) {
      console.error("delete failed", err);
      setActionResult({ severity: "error", text: `Delete failed: ${err.message || err}` });
    } finally {
      setBusyId(null);
      setPendingDelete(null);
    }
  };

  // update_run does a full model_dump() overwrite (no partial PATCH), so the
  // toggle sends the whole row back with just `evaluate` flipped. Extra
  // joined columns (report_status/report_exception*) ride along but Pydantic
  // silently drops unknown fields server-side.
  const toggleEvaluate = async (run) => {
    setBusyId(run.id);
    try {
      await apiClient.put(`/run/${encodeURIComponent(run.id)}`, { ...run, evaluate: !run.evaluate });
      await fetchRuns({ showSpinner: false });
    } catch (err) {
      console.error("evaluate toggle failed", err);
      setActionResult({ severity: "error", text: `Evaluate toggle failed: ${err.message || err}` });
    } finally {
      setBusyId(null);
    }
  };

  // standard_out.log lives in the run's work dir and is served (with auth) by
  // the report-path endpoint, so we can't use a bare <a href>. Fetch it
  // through apiClient (which attaches the JWT) and show it in the same
  // FileViewer-in-a-Dialog pattern the /report-list Outputs tab uses, rather
  // than opening a raw blob URL in a new tab.
  const [logDialog, setLogDialog] = useState(null); // { run, loading, content, error }

  const openStandardOutLog = useCallback(async (run) => {
    if (!run?.user_id || !run?.id) return;
    setLogDialog({ run, loading: true, content: null, error: null });
    try {
      const text = await apiClient.getRaw(
        `/report-path/${encodeURIComponent(run.user_id)}/work/${encodeURIComponent(run.id)}/standard_out.log`,
      );
      const body = text == null
        ? null
        : typeof text === "string" ? text : JSON.stringify(text, null, 2);
      setLogDialog({ run, loading: false, content: body, error: null });
    } catch (err) {
      console.error("Failed to open standard_out.log", err);
      setLogDialog({ run, loading: false, content: null, error: err.message || String(err) });
    }
  }, []);

  const closeStandardOutLog = useCallback(() => setLogDialog(null), []);

  const downloadStandardOutLog = useCallback(() => {
    if (!logDialog?.content || !logDialog?.run) return;
    const url = URL.createObjectURL(new Blob([logDialog.content], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `standard_out-${logDialog.run.id}.log`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [logDialog]);

  // Active runs sort to the top (most recent activity), so on the default
  // activity-desc first page this reflects the live set; on later pages it's
  // scoped to what's visible — hence the "on this page" qualifier below.
  const activeCount = useMemo(
    () => rows.filter((r) => !TERMINAL_STATUSES.has(String(r.status || "").toLowerCase())).length,
    [rows],
  );

  return (
    <PageShell>
      <PageContainer maxWidth="100%">
        <PageHeader
          icon={<PlayCircleOutlineIcon />}
          title="Runs"
          subtitle={`${total} total · ${activeCount} active on this page`}
          action={
            <Button
              variant="outlined"
              onClick={() => fetchRuns()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.4)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {actionResult && (
          <Alert
            severity={actionResult.severity}
            onClose={() => setActionResult(null)}
            sx={{ mb: 2 }}
          >
            {actionResult.text}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter by id / user / product / status / description…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Box>

        {/* Plain opaque container, not the elevated Paper/DataCard treatment —
            this table is wide (11 columns) and needs every pixel it can get;
            a card's shadow/border-radius/overflow:hidden chrome added nothing
            functionally and this page prioritizes showing complete data over
            the decorative card look. */}
        <Box sx={{ width: "100%", backgroundColor: "#fff", overflow: "hidden" }}>
          {loading && rows.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...tableHeaderCellSx, ...stickyStatusCellSx, zIndex: 3 }}>Status</TableCell>
                      <TableCell sx={tableHeaderCellSx} align="center">User</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Run ID</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Product</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Input File</TableCell>
                      <TableCell sx={tableHeaderCellSx} align="center">BioInterpreter</TableCell>
                      <TableCell sx={tableHeaderCellSx} align="center">Evaluate</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Evaluation</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Started</TableCell>
                      <TableCell sx={tableHeaderCellSx}>Elapsed</TableCell>
                      <TableCell sx={{ ...tableHeaderCellSx, ...stickyActionsCellSx, zIndex: 3 }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No runs match.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((r) => {
                        const status = String(r.status || "").toLowerCase();
                        const active = !TERMINAL_STATUSES.has(status);
                        const isBusy = busyId === r.id;
                        const inputFiles = Array.isArray(r.input_files)
                          ? r.input_files.filter(Boolean)
                          : r.input_files
                            ? [r.input_files]
                            : [];
                        const inputLabel = inputFiles.length ? inputFiles.join(", ") : "—";
                        const inputTooltip = inputFiles.length ? inputFiles.join("\n") : "No input files";
                        // The run row's status can lag its report, so the report
                        // (enriched onto each run by run-list) is the source of
                        // truth for "is this run in error". Fall back to the
                        // run's own fields for older rows.
                        const errorText =
                          r.report_exception_traceback || r.report_exception ||
                          r.exception_traceback || r.exception || "";
                        const hasError =
                          r.report_status === "error" || status === "error" || Boolean(errorText);
                        return (
                          <TableRow key={r.id} hover>
                            <TableCell sx={{ ...stickyStatusCellSx, zIndex: 1, backgroundColor: "#fff" }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip
                                  arrow
                                  title={
                                    hasError && errorText ? (
                                      <Box
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          fontFamily: "monospace",
                                          fontSize: 11,
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          maxHeight: 400,
                                          overflow: "auto",
                                        }}
                                      >
                                        {errorText}
                                      </Box>
                                    ) : (
                                      "Open standard_out.log"
                                    )
                                  }
                                >
                                  {/* The whole pill links to the log. color:inherit on the
                                      Link keeps the Chip's own status color (the link itself
                                      never tints the pill). The error only adds red + a
                                      tooltip — it never replaces the status label. */}
                                  <Link
                                    component="button"
                                    type="button"
                                    onClick={() => openStandardOutLog(r)}
                                    disabled={!r.user_id || !r.id}
                                    underline="none"
                                    sx={{
                                      p: 0,
                                      border: 0,
                                      background: "none",
                                      color: "inherit",
                                      display: "inline-flex",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <Chip
                                      label={r.status || "—"}
                                      size="small"
                                      color={hasError ? "error" : (STATUS_COLORS[status] || "default")}
                                      variant={active ? "filled" : "outlined"}
                                      clickable
                                    />
                                  </Link>
                                </Tooltip>
                                {active && <CircularProgress size={14} />}
                              </Stack>
                            </TableCell>
                            <TableCell align="center" sx={{ width: 56 }}>
                              <UserAvatar email={r.user_id} />
                            </TableCell>
                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                              {r.user_id && r.id ? (
                                <Link
                                  component={RouterLink}
                                  to={`/report-list/${encodeURIComponent(r.user_id)}/${encodeURIComponent(r.id)}`}
                                  underline="hover"
                                >
                                  {r.id}
                                </Link>
                              ) : (
                                r.id
                              )}
                            </TableCell>
                            <TableCell>{r.product || "—"}</TableCell>
                            <TableCell>
                              <Tooltip
                                title={inputTooltip}
                                placement="top-start"
                                arrow
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      maxWidth: 520,
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      fontFamily: "monospace",
                                      fontSize: "0.72rem",
                                      lineHeight: 1.45,
                                    },
                                  },
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.72rem",
                                    color: "#475569",
                                    display: "inline-block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 240,
                                    cursor: "help",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {inputLabel}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ p: 0.25 }}>
                              <Tooltip
                                title={
                                  r.use_biointerpreter
                                    ? "BioInterpreter was requested for this run"
                                    : "BioInterpreter was not requested for this run"
                                }
                              >
                                <span>
                                  <Checkbox
                                    checked={!!r.use_biointerpreter}
                                    readOnly
                                    disableRipple
                                    size="small"
                                    sx={{
                                      p: 0,
                                      "&:hover": { backgroundColor: "transparent" },
                                      cursor: "default",
                                    }}
                                    inputProps={{
                                      "aria-label": "BioInterpreter requested",
                                      tabIndex: -1,
                                    }}
                                    onClick={(e) => e.preventDefault()}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ p: 0.25 }}>
                              <Tooltip
                                title={
                                  isAdmin
                                    ? "Flag this run as needing a BioInterpreter evaluation"
                                    : r.evaluate
                                      ? "An Admin flagged this run for evaluation"
                                      : "Not flagged for evaluation"
                                }
                              >
                                <span>
                                  <Checkbox
                                    checked={!!r.evaluate}
                                    disabled={!isAdmin || isBusy}
                                    readOnly={!isAdmin}
                                    disableRipple={!isAdmin}
                                    size="small"
                                    sx={{
                                      p: 0,
                                      ...(isAdmin ? {} : { "&:hover": { backgroundColor: "transparent" }, cursor: "default" }),
                                    }}
                                    inputProps={{
                                      "aria-label": "Needs evaluation",
                                      ...(isAdmin ? {} : { tabIndex: -1 }),
                                    }}
                                    onChange={isAdmin ? () => toggleEvaluate(r) : undefined}
                                    onClick={isAdmin ? undefined : (e) => e.preventDefault()}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {(() => {
                                const env = evaluationByReport.get(r.id);
                                if (!env) {
                                  return (
                                    <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                                      —
                                    </Typography>
                                  );
                                }
                                const s = (env.status || "open").toLowerCase();
                                return (
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Tooltip
                                      title={`${env.entry_count ?? 0} ${env.entry_count === 1 ? "entry" : "entries"}${env.latest_entry_by ? " — latest by " + env.latest_entry_by : ""}`}
                                    >
                                      <Chip
                                        size="small"
                                        label={RUN_STATUS_LABEL[s] || env.status || "Open"}
                                        color={RUN_STATUS_COLOR[s] || "default"}
                                        variant={s === "closed" ? "filled" : "outlined"}
                                      />
                                    </Tooltip>
                                    {env.average_rating != null && (
                                      <Tooltip title={`avg rating ${Number(env.average_rating).toFixed(1)}/5 across BioInterpreter entries`}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                          <Rating value={Number(env.average_rating)} max={5} readOnly precision={0.1} size="small" />
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                );
                              })()}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtStarted(r.start_datetime)}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {elapsed(r.start_datetime, r.end_datetime)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ whiteSpace: "nowrap", width: 140, ...stickyActionsCellSx, zIndex: 1, backgroundColor: "#fff" }}
                            >
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Tooltip title="Stop">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      disabled={isBusy || !active}
                                      onClick={() => setPendingStop(r)}
                                    >
                                      <StopIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Restart (re-queue as new run)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={isBusy}
                                      onClick={() => setPendingRestart(r)}
                                    >
                                      <RestartAltIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Replay without MATLAB (copy this run's Summary_outputs into a new run)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      disabled={isBusy}
                                      onClick={() => setPendingReplay(r)}
                                    >
                                      <FastForwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Timing breakdown">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => setTimingRun(r)}
                                    >
                                      <ScheduleIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={active ? "Stop the run before deleting it" : "Delete"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={isBusy || active}
                                      onClick={() => setPendingDelete(r)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Box>
      </PageContainer>

      <Dialog open={Boolean(pendingStop)} onClose={() => setPendingStop(null)}>
        <DialogTitle>Stop run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Kill the active subprocess (if any) and mark run <code>{pendingStop?.id}</code>{" "}
            as stopped. This does not delete the row.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStop(null)} disabled={Boolean(busyId)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={Boolean(busyId)}
            onClick={() => doStop(pendingStop)}
          >
            Stop
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingRestart)} onClose={() => setPendingRestart(null)}>
        <DialogTitle>Restart run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Queue a fresh run with the same product, input files, and clones as{" "}
            <code>{pendingRestart?.id}</code>. The new run is launched as{" "}
            <code>{pendingRestart?.user_id}</code> (the original submitter), not as the admin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRestart(null)} disabled={Boolean(busyId)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={Boolean(busyId)}
            onClick={() => doRestart(pendingRestart)}
          >
            Restart
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingReplay)} onClose={() => setPendingReplay(null)}>
        <DialogTitle>Replay without MATLAB?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Queue a fresh run with the same product and input files as{" "}
            <code>{pendingReplay?.id}</code>, but skip MATLAB entirely — its
            Summary_outputs and final_outputs are copied over instead, and the rest of
            the pipeline (post-processing, BioInterpreter) runs fresh against them.
            Fails if <code>{pendingReplay?.id}</code> has no Summary_outputs on disk.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingReplay(null)} disabled={Boolean(busyId)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={Boolean(busyId)}
            onClick={() => doReplay(pendingReplay)}
          >
            Replay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(timingRun)} onClose={() => setTimingRun(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Timing breakdown</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            <code>{timingRun?.id}</code>
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellSx}>Stage</TableCell>
                <TableCell sx={tableHeaderCellSx} align="right">Duration</TableCell>
                <TableCell sx={tableHeaderCellSx} align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Queued → Started</TableCell>
                <TableCell align="right">{elapsed(timingRun?.queued_at, timingRun?.start_datetime)}</TableCell>
                <TableCell align="right">—</TableCell>
              </TableRow>
              {(() => {
                const subtasks = timingRun?.subtasks || {};
                const keys = Object.keys(subtasks);
                // Show every known stage even if absent for this run (renders "—"),
                // plus any extra key (e.g. a new biointerpreter variant) not yet
                // in STAGE_ORDER, appended after.
                const extra = keys.filter((k) => !STAGE_ORDER.includes(k)).sort();
                return [...STAGE_ORDER, ...extra].map((key) => {
                  const stage = subtasks[key];
                  return (
                    <TableRow key={key}>
                      <TableCell>{stageLabel(key)}</TableCell>
                      <TableCell align="right">
                        {stage ? elapsed(stage.started_at, stage.finished_at) : "—"}
                        {stage?.status === "running" && (
                          <Chip label="running" size="small" color="info" sx={{ ml: 1 }} />
                        )}
                        {stage?.status === "error" && (
                          <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="right" title={formatUsage(stage?.usage) || undefined}>
                        {formatCost(stage?.usage?.cost_usd) || "—"}
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {totalElapsed(timingRun)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatCost(totalCost(timingRun?.subtasks)) || "—"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
            Stages showing "—" weren't recorded for this run — either it predates this
            timing feature, or that stage wasn't part of this run. Cost is what an AI agent
            stage reported (hover for tokens); runs before 2026-10-03 didn't record it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimingRun(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete run <code>{pendingDelete?.id}</code> from the database. This
            does not remove files on disk under the user's work directory.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={Boolean(busyId)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(busyId)}
            onClick={() => doDelete(pendingDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(logDialog)} onClose={closeStandardOutLog} maxWidth="md" fullWidth>
        <DialogTitle>standard_out.log — run {logDialog?.run?.id}</DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          {logDialog?.error ? (
            <Alert severity="error">Could not load log: {logDialog.error}</Alert>
          ) : !logDialog?.loading && logDialog?.content == null ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No standard_out.log available yet for run {logDialog?.run?.id}.
            </Typography>
          ) : (
            <FileViewer
              fileContent={logDialog?.content}
              selectedFile="standard_out.log"
              loading={Boolean(logDialog?.loading)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={downloadStandardOutLog} disabled={!logDialog?.content}>
            Download
          </Button>
          <Button onClick={closeStandardOutLog}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
